import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import type { AddendumMacro } from "../../../services/addendum.service";
import type { AddendumMacroInput } from "../../../services/admin/addendumMacros.service";
import { isEmptyHtml } from "../../../utils/helpers";
import { scrollToFirstError } from "../../../utils/formValidation";
import RichTextDiagnosis from "../../pathologist-review/RichTextDiagnosis";
import { ConfirmAddendumActionModal } from "../../pathologist-review/ConfirmAddendumActionModal";

interface AddendumMacroFormModalProps {
  opened: boolean;
  macro: AddendumMacro | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (values: AddendumMacroInput) => Promise<void>;
}

function buildInitialValues(macro: AddendumMacro | null): AddendumMacroInput {
  return {
    name: macro?.name || "",
    shortcut: macro?.shortcut || null,
    contentHtml: macro?.contentHtml || "",
    macroType: macro?.macroType || "addendum",
    isActive: macro?.isActive ?? true,
  };
}

export function AddendumMacroFormModal({
  opened,
  macro,
  loading,
  error,
  onClose,
  onSubmit,
}: AddendumMacroFormModalProps) {
  const [discardOpened, setDiscardOpened] = useState(false);
  const form = useForm<AddendumMacroInput>({
    initialValues: buildInitialValues(macro),
    validateInputOnBlur: true,
    validate: {
      name: (value) => (value.trim() ? null : "Macro name is required"),
      contentHtml: (value) =>
        isEmptyHtml(value) ? "Macro content is required" : null,
    },
  });

  const handleContentChange = useCallback(
    (contentHtml: string) => {
      form.setFieldValue("contentHtml", contentHtml);
      form.clearFieldError("contentHtml");
    },
    [form]
  );

  const handleSubmit = useCallback(
    async (values: AddendumMacroInput) => {
      await onSubmit({
        ...values,
        name: values.name.trim(),
        shortcut: values.shortcut?.trim() || null,
      });
    },
    [onSubmit]
  );
  const handleSubmitError = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);
  const handleRequestClose = useCallback(() => {
    if (form.isDirty()) {
      setDiscardOpened(true);
      return;
    }
    onClose();
  }, [form, onClose]);
  const handleKeepEditing = useCallback(() => {
    setDiscardOpened(false);
  }, []);
  const handleDiscard = useCallback(() => {
    setDiscardOpened(false);
    onClose();
  }, [onClose]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleRequestClose}
        title={macro ? "Edit Report Macro" : "Add Report Macro"}
        size="lg"
        centered
        closeOnClickOutside={false}
        closeOnEscape={!loading}
      >
        <form onSubmit={form.onSubmit(handleSubmit, handleSubmitError)}>
          <Stack>
            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Macro could not be saved"
                color="red"
              >
                {error}
              </Alert>
            )}
            <TextInput
              label="Name"
              placeholder="e.g. Additional immunostain results"
              withAsterisk
              {...form.getInputProps("name")}
            />
            <TextInput
              label="Shortcut"
              description="Optional shorthand used to find the macro."
              placeholder="e.g. IHCADD"
              {...form.getInputProps("shortcut")}
            />
            <Select
              label="Macro type"
              description="Controls which signed-report editor offers this macro."
              data={[
                { value: "addendum", label: "Addendum" },
                { value: "amendment", label: "Amendment" },
              ]}
              allowDeselect={false}
              withAsterisk
              {...form.getInputProps("macroType")}
            />
            <RichTextDiagnosis
              label="Macro content"
              withAsterisk
              value={form.values.contentHtml}
              onChange={handleContentChange}
              minHeight={180}
              placeholder="Enter reusable report text. Add variables as {{variable_name}}."
              error={form.errors.contentHtml as string | undefined}
              supplementalTypography="toolbar"
              paragraphSpacing={
                form.values.macroType === "addendum" ? "single-line" : "default"
              }
            />
            <Text size="xs" c="dimmed">
              Variables written as {"{{variable_name}}"} become fillable fields
              that pathologists can navigate with Tab and Shift+Tab.
            </Text>
            <Switch
              label="Active"
              description="Only active macros appear in the selected report editor."
              {...form.getInputProps("isActive", { type: "checkbox" })}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={handleRequestClose}>
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                {macro ? "Save Changes" : "Create Macro"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
      <ConfirmAddendumActionModal
        opened={discardOpened}
        title="Discard unsaved macro changes?"
        message="Changes made since opening the macro editor will be lost."
        confirmLabel="Discard Changes"
        onCancel={handleKeepEditing}
        onConfirm={handleDiscard}
      />
    </>
  );
}
